import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowUpRight, ArrowDownRight, Plus, Search, Trash2,
  Edit2, Loader2, Calendar, DollarSign, ArrowLeft, Save,
  FileText, Receipt, Download, X, Check, PlusCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const paymentMethods = ["Cash", "Bank Transfer", "Mobile Money", "Card", "Check", "Other"];

export default function CashLedger() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  
  const [newTransaction, setNewTransaction] = useState({
    description: "",
    transaction_date: new Date().toISOString().split("T")[0],
    cash_date: new Date().toISOString().split("T")[0],
    transaction_type: "expense",
    category: "",
    category_id: "",
    payment_method: "Cash",
    amount: "",
    status: "paid",
    notes: ""
  });

  useEffect(() => {
    loadTransactions();
    loadCategories();
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [startDate, endDate]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      let url = `${API}/cash/transactions?limit=200`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      const response = await axios.get(url);
      setTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load transactions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  // Get filtered categories based on transaction type
  const filteredCategories = categories.filter(cat => 
    cat.type === newTransaction.transaction_type &&
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Check if typed category exists
  const categoryExists = filteredCategories.some(
    cat => cat.name.toLowerCase() === categorySearch.toLowerCase()
  );

  // Create new category inline
  const handleCreateCategory = async () => {
    if (!categorySearch.trim()) return;
    
    setCreatingCategory(true);
    try {
      const response = await axios.post(`${API}/categories`, {
        name: categorySearch.trim(),
        type: newTransaction.transaction_type
      });
      
      // Add to local categories list
      setCategories(prev => [...prev, response.data]);
      
      // Set as selected category
      setNewTransaction(prev => ({
        ...prev,
        category: response.data.name,
        category_id: response.data.id
      }));
      
      setCategoryOpen(false);
      setCategorySearch("");
      toast.success(`Category "${response.data.name}" created`);
    } catch (error) {
      toast.error("Failed to create category");
      console.error(error);
    } finally {
      setCreatingCategory(false);
    }
  };

  // Select existing category
  const handleSelectCategory = (category) => {
    setNewTransaction(prev => ({
      ...prev,
      category: category.name,
      category_id: category.id
    }));
    setCategoryOpen(false);
    setCategorySearch("");
  };

  const handleSaveTransaction = async () => {
    if (!newTransaction.description || !newTransaction.amount) {
      toast.error("Please fill in description and amount");
      return;
    }
    
    if (!newTransaction.category) {
      toast.error("Please select a category");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount),
        source_type: "manual"
      };
      
      if (editingTransaction) {
        await axios.put(`${API}/cash/transactions/${editingTransaction.id}`, payload);
        toast.success("Transaction updated");
      } else {
        await axios.post(`${API}/cash/transactions`, payload);
        toast.success("Transaction added");
      }
      
      setShowAddDialog(false);
      setEditingTransaction(null);
      resetForm();
      loadTransactions();
    } catch (error) {
      toast.error("Failed to save transaction");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/cash/transactions/${id}`);
      toast.success("Transaction deleted");
      loadTransactions();
    } catch (error) {
      toast.error("Failed to delete transaction");
      console.error(error);
    }
  };

  const openEditDialog = (trans) => {
    setEditingTransaction(trans);
    setNewTransaction({
      description: trans.description,
      transaction_date: trans.transaction_date,
      cash_date: trans.cash_date,
      transaction_type: trans.transaction_type,
      category: trans.category,
      category_id: trans.category_id || "",
      payment_method: trans.payment_method,
      amount: trans.amount.toString(),
      status: trans.status,
      notes: trans.notes || ""
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setNewTransaction({
      description: "",
      transaction_date: new Date().toISOString().split("T")[0],
      cash_date: new Date().toISOString().split("T")[0],
      transaction_type: "expense",
      category: "",
      category_id: "",
      payment_method: "Cash",
      amount: "",
      status: "paid",
      notes: ""
    });
    setCategorySearch("");
  };

  const handleTypeChange = (type) => {
    setNewTransaction(prev => ({ 
      ...prev, 
      transaction_type: type,
      category: "",
      category_id: ""
    }));
    setCategorySearch("");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  const filteredTransactions = transactions.filter(trans => {
    const matchesSearch = 
      trans.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trans.category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === "all" || trans.transaction_type === filterType;
    
    return matchesSearch && matchesType;
  });

  const totalInflows = filteredTransactions
    .filter(t => t.transaction_type === "income" && t.status === "paid")
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalOutflows = filteredTransactions
    .filter(t => t.transaction_type === "expense" && t.status === "paid")
    .reduce((sum, t) => sum + t.amount, 0);

  const exportCSV = () => {
    const headers = ["Date", "Description", "Type", "Category", "Amount", "Status", "Payment Method", "Notes"];
    const rows = filteredTransactions.map(t => [
      t.cash_date || t.transaction_date,
      `"${t.description.replace(/"/g, '""')}"`,
      t.transaction_type,
      t.category,
      t.amount,
      t.status,
      t.payment_method,
      `"${(t.notes || "").replace(/"/g, '""')}"`
    ]);
    
    const csv = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cash-ledger-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setShowDateFilters(false);
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="cash-ledger">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Cash Ledger</h1>
            <p className="text-sm text-muted-foreground">
              {filteredTransactions.length} transactions
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={exportCSV} data-testid="export-csv-btn" className="h-9">
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </Button>
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingTransaction(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-9" data-testid="add-entry-btn">
                <Plus className="w-4 h-4 mr-1.5" />
                Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {editingTransaction ? "Edit Transaction" : "New Entry"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-2">
                {/* Transaction Type Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleTypeChange("income")}
                    className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      newTransaction.transaction_type === "income"
                        ? "bg-emerald-500 text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid="type-income"
                  >
                    <ArrowUpRight className="w-4 h-4 inline mr-1" />
                    Income
                  </button>
                  <button
                    onClick={() => handleTypeChange("expense")}
                    className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      newTransaction.transaction_type === "expense"
                        ? "bg-red-500 text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid="type-expense"
                  >
                    <ArrowDownRight className="w-4 h-4 inline mr-1" />
                    Expense
                  </button>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input
                    value={newTransaction.description}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What was this for?"
                    className="mt-1"
                    data-testid="trans-description"
                  />
                </div>

                {/* Amount & Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Amount</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={newTransaction.amount}
                        onChange={(e) => setNewTransaction(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        className="pl-7"
                        data-testid="trans-amount"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <Input
                      type="date"
                      value={newTransaction.cash_date}
                      onChange={(e) => setNewTransaction(prev => ({ 
                        ...prev, 
                        cash_date: e.target.value,
                        transaction_date: e.target.value
                      }))}
                      className="mt-1"
                      data-testid="trans-date"
                    />
                  </div>
                </div>

                {/* Category (Searchable with inline add) */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={categoryOpen}
                          className="w-full justify-between mt-1 font-normal"
                          data-testid="trans-category"
                        >
                          {newTransaction.category || "Select category..."}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Search or add..." 
                            value={categorySearch}
                            onValueChange={setCategorySearch}
                            data-testid="category-search"
                          />
                          <CommandList>
                            <CommandEmpty className="py-2 px-3 text-sm text-muted-foreground">
                              No category found
                            </CommandEmpty>
                            <CommandGroup>
                              {filteredCategories.map((cat) => (
                                <CommandItem
                                  key={cat.id}
                                  value={cat.name}
                                  onSelect={() => handleSelectCategory(cat)}
                                  className="cursor-pointer"
                                  data-testid={`category-option-${cat.name}`}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      newTransaction.category === cat.name ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  {cat.name}
                                  {cat.system_defined && (
                                    <span className="ml-auto text-xs text-muted-foreground">default</span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            
                            {/* Add new category option */}
                            {categorySearch && !categoryExists && (
                              <CommandGroup>
                                <CommandItem
                                  onSelect={handleCreateCategory}
                                  className="cursor-pointer text-primary"
                                  data-testid="add-category-option"
                                >
                                  {creatingCategory ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <PlusCircle className="mr-2 h-4 w-4" />
                                  )}
                                  Add "{categorySearch}"
                                </CommandItem>
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Payment Method</Label>
                    <Select 
                      value={newTransaction.payment_method} 
                      onValueChange={(v) => setNewTransaction(prev => ({ ...prev, payment_method: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="trans-payment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map(method => (
                          <SelectItem key={method} value={method}>{method}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select 
                    value={newTransaction.status} 
                    onValueChange={(v) => setNewTransaction(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="trans-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                  <Textarea
                    value={newTransaction.notes}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional details..."
                    rows={2}
                    className="mt-1 resize-none"
                  />
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveTransaction} 
                  disabled={saving}
                  className="w-full"
                  data-testid="save-transaction-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {editingTransaction ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Row */}
      <div className="flex items-center gap-8 mb-6 pb-6 border-b border-border">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">In</p>
          <p className="text-xl font-semibold text-emerald-600 tabular-nums">+{formatCurrency(totalInflows)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Out</p>
          <p className="text-xl font-semibold text-red-500 tabular-nums">-{formatCurrency(totalOutflows)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Net</p>
          <p className={`text-xl font-semibold tabular-nums ${totalInflows - totalOutflows >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {formatCurrency(totalInflows - totalOutflows)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
            data-testid="ledger-search"
          />
        </div>
        
        <div className="flex items-center gap-1 bg-muted/50 rounded-md p-0.5">
          <button
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              filterType === "all" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setFilterType("all")}
          >
            All
          </button>
          <button
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              filterType === "income" ? "bg-background shadow-sm font-medium text-emerald-600" : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setFilterType("income")}
          >
            Income
          </button>
          <button
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              filterType === "expense" ? "bg-background shadow-sm font-medium text-red-500" : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setFilterType("expense")}
          >
            Expenses
          </button>
        </div>
        
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
            showDateFilters ? "bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setShowDateFilters(!showDateFilters)}
          data-testid="toggle-date-filters"
        >
          <Calendar className="w-3.5 h-3.5" />
          Date
        </button>
      </div>

      {/* Date Filters */}
      {showDateFilters && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-muted/30 rounded-md" data-testid="date-filters">
          <span className="text-xs text-muted-foreground">From</span>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-36 h-8"
            data-testid="start-date"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-36 h-8"
            data-testid="end-date"
          />
          {(startDate || endDate) && (
            <button 
              onClick={clearDateFilters}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Transactions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sm text-muted-foreground mb-3">
            {searchQuery ? "No matching transactions" : "No transactions yet"}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowAddDialog(true)} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-1.5" />
              Add First Entry
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-0">
          {filteredTransactions.map((trans, index) => (
            <div 
              key={trans.id}
              className={`flex items-center gap-3 py-3 group ${
                index !== 0 ? "border-t border-border/50" : ""
              }`}
              data-testid={`transaction-${trans.id}`}
            >
              {/* Icon */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                trans.transaction_type === "income" 
                  ? "bg-emerald-100" 
                  : "bg-red-100"
              }`}>
                {trans.source_type === "invoice" ? (
                  <FileText className={`w-4 h-4 ${trans.transaction_type === "income" ? "text-emerald-600" : "text-red-500"}`} />
                ) : trans.source_type === "receipt" ? (
                  <Receipt className={`w-4 h-4 ${trans.transaction_type === "income" ? "text-emerald-600" : "text-red-500"}`} />
                ) : trans.transaction_type === "income" ? (
                  <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                )}
              </div>
              
              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{trans.description}</p>
                  {trans.status === "pending" && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pending</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {trans.category} · {trans.payment_method} · {formatDate(trans.cash_date)}
                </p>
              </div>
              
              {/* Amount */}
              <p className={`text-sm font-semibold tabular-nums ${
                trans.transaction_type === "income" ? "text-emerald-600" : "text-red-500"
              }`}>
                {trans.transaction_type === "income" ? "+" : "-"}
                {formatCurrency(trans.amount)}
              </p>
              
              {/* Actions */}
              {trans.source_type === "manual" && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => openEditDialog(trans)}
                    data-testid={`edit-${trans.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" data-testid={`delete-${trans.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently remove this transaction.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteTransaction(trans.id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
